/*
 * Copyright (c) Nexign, JSC, 1992-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Dmitriy Kvon
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.16.0
 */

import { useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { makeSelectAuthoritiesIsServiceUser } from '@/main/pages/PageAuth/selectors/authSelectors';
import type { ISubscriberIdContext } from '@/main/pages/PageSubscriberId/context';
import { subscriberIdNameSelector, subscriberIdNumberSelector } from '@/main/pages/PageSubscriberId/selectors';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { Button, LinkBlock } from '@components/common';
import { ImgStatusError, ImgStatusSuccess } from '@components/common/Block/Img';
import { formatNumberToPhone } from '@components/common/PhoneMask/formatNumberToPhone';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { ELocaleKeys } from '@modules/internationalization/intConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import Modal from '@modules/modal/components/Modal';
import { ModalCenterStyled } from '@modules/modal/components/styled/ModalCenterStyled';
import { modalSetStatusAction } from '@modules/modal/modalActions';
import { EModalStatus, IModalRenderProps } from '@modules/modal/types/modalTypes';
import { PersonalDataDeleteModalStyled } from '@modules/personalDataDelete/components/styled/PersonalDataDeleteModalStyled';

import { personalDataTranslationSelector } from '../selectors';
import { useDeletePersonalDataMutation } from '../store';


const SubscriberIdDeleteModal = ({
    onCloseModal,
    subscriberId,
    userType,
}: Pick<IModalRenderProps, 'onCloseModal'> & ISubscriberIdContext) => {

    const dispatch = useDispatch();

    const number = useSelector(subscriberIdNumberSelector(subscriberId, userType));
    const name = useSelector(subscriberIdNameSelector(subscriberId, userType));

    const tabTranslation = useSelector(personalDataTranslationSelector());
    const translationCommon = useSelector(makeSelectTranslationCommon());

    const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());

    const [textFromConflict, setTextFromConflict] = useState('');

    const [deletePersonalData] = useDeletePersonalDataMutation();

    const onDelete = () => {
        dispatch(modalSetStatusAction({ status: EModalStatus.processing }));
        deletePersonalData(subscriberId)
            .unwrap()
            .then(({ conflicts }) => {
                if (conflicts?.length) {
                    const text = conflicts.map(conflict =>
                        translationCommon.error.codes[conflict.code] || conflict.message).join('\n\n');
                    setTextFromConflict(text ?? translationCommon.error.defaultMessage);

                    dispatch(modalSetStatusAction({ status: EModalStatus.failure }));
                } else {
                    dispatch(modalSetStatusAction({ status: EModalStatus.success }));
                }
            })
            .catch(() => {
                dispatch(modalSetStatusAction({ status: EModalStatus.failure }));
            });
    };

    return (
        <Modal
            withoutPadding
            dataName="personal-data-delete-modal"
        >
            <PersonalDataDeleteModalStyled>

                <Modal.Status status={EModalStatus.start}>
                    <Modal.Title className="delete-modal__title">
                        {tabTranslation.deleteModal[EModalStatus.start].title}
                    </Modal.Title>
                    <Modal.Text className="delete-modal__subtitle">
                        {stringFormatParams(tabTranslation.deleteModal[EModalStatus.start].subtitle, {
                            [ELocaleKeys.number]: formatNumberToPhone(number),
                            [ELocaleKeys.name]: name,
                        })}
                    </Modal.Text>
                    <Modal.Body highlightBg className="delete-modal__body">
                        <Modal.Text>
                            <div dangerouslySetInnerHTML={{
                                __html: tabTranslation.deleteModal[EModalStatus.start].text,
                            }} />
                        </Modal.Text>
                    </Modal.Body>
                    <Modal.Control>
                        <ArrowTooltip disableHoverListener={!isServiceUser}
                            message={translationCommon.serviceUser.tooltip}
                            placement="top"
                        >
                            <span>
                                <Button
                                    dataName="personal-data-delete-modal-btn-agree"
                                    disabled={isServiceUser}
                                    kind="medium"
                                    onClick={onDelete}
                                >
                                    {translationCommon.modal.remove}
                                </Button>
                            </span>
                        </ArrowTooltip>
                        <LinkBlock
                            dataName="personal-data-delete-modal-btn-cancel"
                            onClick={onCloseModal}
                        >
                            {translationCommon.modal.cancel}
                        </LinkBlock>
                    </Modal.Control>
                </Modal.Status>
                <Modal.Status status={EModalStatus.success}>
                    <ModalCenterStyled>
                        <ImgStatusSuccess />
                        <Modal.Title>{tabTranslation.deleteModal[EModalStatus.success].title}</Modal.Title>
                        <Button
                            dataName="personal-data-delete-modal-btn-success"
                            kind="medium"
                            onClick={onCloseModal}
                        >
                            {translationCommon.modal.good}
                        </Button>
                    </ModalCenterStyled>
                </Modal.Status>
                <Modal.Status status={EModalStatus.failure}>
                    <ModalCenterStyled>
                        <ImgStatusError />
                        <Modal.Title>
                            {tabTranslation.deleteModal[EModalStatus.failure].title}
                        </Modal.Title>
                        <Modal.Text className="delete-modal__failure-text">
                            {textFromConflict}
                        </Modal.Text>
                        <Button
                            dataName="personal-data-delete-modal-btn-failure"
                            kind="medium"
                            onClick={onCloseModal}
                        >
                            {translationCommon.modal.accessibly}
                        </Button>
                    </ModalCenterStyled>
                </Modal.Status>
            </PersonalDataDeleteModalStyled>
        </Modal>
    );
};

export default SubscriberIdDeleteModal;
